/**
 * Open a new revision on a document.
 *
 * The reason for issue is mandatory because "Rev C" alone tells a reviewer
 * nothing about what changed. The status code is optional here: a revision
 * normally gets its code back from review, not at the moment it is opened.
 */
import { useState } from 'react';
import { Loader2, X } from 'lucide-react';
import api from '@/lib/api';

export const EdmOpenRevisionDialog = ({ t, documentId, nextRevision, statusCodes = [],
  onClose, onOpened }) => {
  const [reason, setReason] = useState('');
  const [statusCode, setStatusCode] = useState('');
  const [issueDate, setIssueDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const inputStyle = { background: t.page, border: `1px solid ${t.border}`, color: t.ink };
  const inputCls = 'mt-1 w-full rounded-lg px-3 py-2 text-sm outline-none';

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const r = await api.post(`/edm/documents/${documentId}/revisions`, {
        reason_for_issue: reason,
        status_code: statusCode || null,
        issue_date: issueDate ? new Date(issueDate).toISOString() : null,
      });
      onOpened(r.data);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || 'Could not open the revision');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-6"
      style={{ background: '#000000CC' }} data-testid="edm-revision-dialog">
      <form onSubmit={submit} className="w-full max-w-xl rounded-xl p-6"
        style={{ background: t.surface, border: `1px solid ${t.border}` }}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-base md:text-lg font-semibold" style={{ color: t.ink }}>
              Open revision {nextRevision || ''}
            </h2>
            <p className="mt-1 text-[11px]" style={{ color: t.muted }}>
              The code is worked out from the scheme and the prior revisions. Undispositioned
              comments on the previous revision are carried forward automatically.
            </p>
          </div>
          <button type="button" onClick={onClose} data-testid="edm-revision-dialog-close"
            style={{ color: t.muted }}><X size={16} /></button>
        </div>

        <label className="mt-5 block">
          <span className="font-mono text-[10px] uppercase tracking-wider" style={{ color: t.muted }}>
            Reason for issue *
          </span>
          <textarea required rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
            data-testid="edm-revision-reason"
            placeholder="Re-issued for approval — flare header rerouted around the new KO drum."
            className={inputCls} style={inputStyle} />
        </label>

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-wider" style={{ color: t.muted }}>
              Status code
            </span>
            <select value={statusCode} onChange={(e) => setStatusCode(e.target.value)}
              data-testid="edm-revision-status" className={inputCls} style={inputStyle}>
              <option value="">none yet — in review</option>
              {statusCodes.map((s) => (
                <option key={s.code} value={s.code}>{s.code} · {s.label}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-wider" style={{ color: t.muted }}>
              Issue date
            </span>
            <input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)}
              data-testid="edm-revision-issue-date" className={inputCls} style={inputStyle} />
          </label>
        </div>

        {err && <div className="mt-4 text-[12px]" style={{ color: t.red }}
          data-testid="edm-revision-dialog-error">{err}</div>}

        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-lg px-3 py-2 text-xs"
            style={{ border: `1px solid ${t.border}`, color: t.body }}>Cancel</button>
          <button type="submit" disabled={busy} data-testid="edm-revision-dialog-submit"
            className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold"
            style={{ background: t.primary, color: t.page, opacity: busy ? 0.6 : 1 }}>
            {busy && <Loader2 size={12} className="animate-spin" />} Open revision
          </button>
        </div>
      </form>
    </div>
  );
};

export default EdmOpenRevisionDialog;
